/**
 * BuilderHeader — adapter avatar + switcher, version select + status pill +
 * rev chip + add-version, logs button, builder-status pill, approve button.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { Chart, Check, Plus } from '../../icons';
import { Button, Select } from '../../components';
import { useBridge } from '../../store/bridgeStore';
import { useBuilder } from './useBuilder';

export default function BuilderHeader() {
  const { actions } = useBridge();
  const b = useBuilder();

  const adapterOptions = [...b.builderAdapterOptions, { value: '__new__', label: '+ New integration…' }];

  return (
    <View style={styles.header}>
      <View style={[styles.avatar, { backgroundColor: b.adapter.color } as any]}><Text style={styles.avatarText}>{b.adapter.initials}</Text></View>

      <View>
        <Select value={b.adapter.id} options={adapterOptions} onChange={v => actions.pickAdapter(v)} variant="plain" title="Switch connector" />
        <Text className="t-mono" style={styles.source}>{b.classLabel + ' adapter · ' + b.sourceLabel}</Text>
      </View>

      {/* version cluster */}
      <View style={styles.versionCluster}>
        <Text style={styles.verLabel}>VERSION</Text>
        <Select value={b.selVersionId || ''} options={b.versionOptions} onChange={v => actions.selectVersion(v)} variant="group" height={30} />
        <View style={[styles.verPill, { borderColor: `var(--${b.verTone}-line)`, backgroundColor: `var(--${b.verTone}-soft)` } as any]}>
          <View style={[styles.verDot, { backgroundColor: `var(--${b.verTone})` } as any]} />
          <Text style={[styles.verPillText, { color: `var(--${b.verTone})` } as any]}>{b.verStatusLabel}</Text>
        </View>
        <View style={styles.revChip}><Text style={styles.revChipText}>{'rev ' + b.verRev}</Text></View>
        <Pressable onPress={() => actions.openAddVersion()} style={styles.addVer} ><Plus size={14} color={colors.ink2} /></Pressable>
      </View>

      <View style={{ flex: 1 }} />

      <Button label={'Logs · ' + b.verExecs} variant="secondary" height={34} onPress={() => actions.openLogs(b.adapter.id)} left={<Chart size={14} color={colors.ink2} />} />

      <View style={[styles.statusPill, { borderColor: `var(--${b.builderTone}-line)`, backgroundColor: `var(--${b.builderTone}-soft)` } as any]}>
        <View style={[styles.statusDot, { backgroundColor: `var(--${b.builderTone})` } as any]} />
        <Text style={[styles.statusText, { color: `var(--${b.builderTone})` } as any]}>{b.builderStatus}</Text>
      </View>

      {b.showApprove && (
        <Button label="Review & approve" height={34} onPress={() => actions.openApprove()} left={<Check size={14} color="#fff" />} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexShrink: 0, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: colors.card, paddingVertical: 12, paddingHorizontal: 20, flexDirection: 'row', alignItems: 'center', gap: 16 },
  avatar: { width: 36, height: 36, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, color: '#fff', fontSize: fontSize.md },
  source: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  versionCluster: { marginLeft: 8, flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 14, borderLeftWidth: 1, borderLeftColor: colors.line },
  verLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4 },
  verPill: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 26, paddingHorizontal: 10, borderRadius: radii.full, borderWidth: 1 },
  verDot: { width: 6, height: 6, borderRadius: 3 },
  verPillText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  revChip: { height: 26, paddingHorizontal: 10, borderRadius: radii.r2, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper3, alignItems: 'center', justifyContent: 'center' },
  revChipText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: colors.ink2 },
  addVer: { height: 30, width: 30, alignItems: 'center', justifyContent: 'center', borderRadius: radii.r2, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.lineStrong, backgroundColor: colors.card, cursor: 'pointer' } as any,
  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 26, paddingHorizontal: 12, borderRadius: radii.full, borderWidth: 1 },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  statusText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
});
